import {ChangeDetectionStrategy, Component, EventEmitter, Input, Output} from '@angular/core';
import {ImportModuleProfileSummary} from '../../../core/definitions/import-module';
import {IMPORT_STEPS, ImportStep, ImportView, isStepOpen, StepProgress, StepState, stepOfView, stepState}
  from './import-steps';

/**
 * The Import module page's step bar: the four steps in order, each a
 * circle beside its name and a line on how far it has come, drawn by its
 * `StepState`, with a step that cannot be opened yet disabled; and the
 * profile: its name, which opens the saved profiles to pick one from, a
 * dot while the configuration differs from the profile last applied or
 * saved, Lagre profil, and in a menu Lagre som ny, Slett, Eksporter profil
 * and Importer profil, which asks for a `.json` file.
 */
@Component({
  selector: 'app-import-step-bar',
  templateUrl: './import-step-bar.component.html',
  styleUrls: ['./import-step-bar.component.scss'],
  standalone: false,
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class ImportStepBarComponent {
  @Input({required: true}) view!: ImportView;
  /** The mapping step can be opened. */
  @Input() mappingOpen = false;
  /** The steps after the mapping step can be opened. */
  @Input() laterOpen = false;
  /** The source table and the object type's name key, under the first
   * step. */
  @Input() sourceTable: string | null = null;
  @Input() objectTypeName: string | null = null;
  /** The mapping's lines. */
  @Input() mappingLines = 0;
  /** The source columns the run reads. */
  @Input() usedColumns = 0;
  /** The source table's columns; none hides the mapping step's summary. */
  @Input() columnCount = 0;
  /** Values the value review has left undecided; `null` before a scan. */
  @Input() undecidedValues: number | null = null;
  /** Rows the shown Forhåndskontroll warned about; `null` while none is
   * shown. */
  @Input() warnedRows: number | null = null;
  /** A run of this import has finished. */
  @Input() runFinished = false;
  /** The loaded profile's name; `null` when none is loaded. */
  @Input() profileName: string | null = null;
  /** The saved profiles the name offers; with none, the name is plain text. */
  @Input() profiles: ImportModuleProfileSummary[] = [];
  /** The loaded profile's id, marked among `profiles`. */
  @Input() profileId: string | null = null;
  /** A save would change the profile last applied or saved. */
  @Input() unsaved = false;
  /** There is a configuration to save as a new profile. */
  @Input() canSaveAsNew = false;
  /** A saved profile is loaded, which Slett deletes. */
  @Input() profileLoaded = false;
  /** A save, a delete or a profile load is in progress. */
  @Input() busy = false;

  /** Asks for the first view of a step. */
  @Output() viewChange = new EventEmitter<ImportView>();
  @Output() save = new EventEmitter<void>();
  @Output() saveAsNew = new EventEmitter<void>();
  @Output() deleteProfile = new EventEmitter<void>();
  /** Asks to save the loaded profile, as stored, as a file. */
  @Output() exportProfile = new EventEmitter<void>();
  /** Hands over the file chosen to import as a profile. */
  @Output() importProfile = new EventEmitter<File>();
  /** Asks to load the profile with this id, or for none with `null`. */
  @Output() pickProfile = new EventEmitter<string | null>();

  readonly steps = IMPORT_STEPS;

  get currentStep(): ImportStep {
    return stepOfView(this.view);
  }

  private get progress(): StepProgress {
    return {
      gates: {mapping: this.mappingOpen, later: this.laterOpen},
      undecidedValues: this.undecidedValues,
      warnedRows: this.warnedRows,
      runFinished: this.runFinished,
    };
  }

  stateOf(step: ImportStep): StepState {
    return stepState(step.key, this.currentStep.key, this.progress);
  }

  isOpen(step: ImportStep): boolean {
    return isStepOpen(step.key, this.progress.gates);
  }

  /** Hands over the file the file input holds, and empties the input so
   * that the same file can be chosen again. */
  onProfileFileChosen(event: Event): void {
    const input = event.target as HTMLInputElement;
    const file = input.files?.[0];
    input.value = '';
    if (file) {
      this.importProfile.emit(file);
    }
  }

  open(step: ImportStep): void {
    if (step !== this.currentStep && this.isOpen(step)) {
      this.viewChange.emit(step.views[0]);
    }
  }
}
